import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogOut, User, Users, Shield, Truck, Sparkles } from "lucide-react";
import { LoginMenu } from "@/UserApp";
import { useAuth } from "@/context/AuthProvider";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);
  // Auto-collapse the mobile menu whenever the route changes.
  useEffect(() => { setOpen(false); }, [location.pathname]);
  // Section links (#catalog, #faq…) only exist on the homepage. From any other
  // route (e.g. /deals) send the user back to "/" with the hash so Home can
  // scroll to the section — otherwise the links appear dead.
  const onHome = location.pathname === "/";
  const hashHref = (h) => (onHome ? h : `/${h}`);
  const goHash = (e, h) => {
    if (!onHome) {
      e.preventDefault();
      nav(`/${h}`);
    }
  };
  const links = [
    { href: "#why-us", label: "Why Us" },
    { href: "/strollers", label: "Strollers", route: true },
    { href: "/gear", label: "Baby Gear", route: true },
    { href: "/ecvs", label: "ECVs", route: true },
    { href: "/deals", label: "Deals", route: true, icon: Sparkles },
    { href: "/delivery", label: "Delivery", route: true },
    { href: "/about", label: "About", route: true },
    { href: "/faq", label: "FAQ", route: true },
    { href: "/contact", label: "Contact", route: true },
  ];
  return (
    <header
      data-testid="site-nav"
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "glass-nav py-3" : "py-5"}`}
      style={{ top: "var(--promo-banner-h, 0px)" }}
    >
      <div className="container flex items-center justify-between">
        <a href={hashHref("#top")} onClick={(e) => goHash(e, "#top")} className="flex items-center gap-3 group" data-testid="nav-logo">
          <span
            className="font-serif text-xl md:text-2xl tracking-wide"
            style={{
              background: "linear-gradient(135deg, #F4A93A 0%, #FFD166 50%, #D97B37 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              filter: "drop-shadow(0 0 8px rgba(255,209,102,0.4))",
            }}
          >
            Enchanted
          </span>
          <span className="text-[#B9C8D5] text-[9px] sm:text-xs uppercase tracking-[0.25em] sm:tracking-[0.28em]">Gear Rentals</span>
        </a>
        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => {
            const Icon = l.icon;
            const className = `text-sm ${l.route ? "text-[#FFD166]" : "text-[#B9C8D5]"} hover:text-[#FFD166] transition-colors duration-200 tracking-wide inline-flex items-center gap-1`;
            return l.route ? (
              <Link key={l.href} to={l.href} data-testid={`nav-link-${l.label.toLowerCase()}`} className={className}>
                {Icon && <Icon className="w-3.5 h-3.5" />} {l.label}
              </Link>
            ) : (
              <a key={l.href} href={hashHref(l.href)} onClick={(e) => goHash(e, l.href)} data-testid={`nav-link-${l.label.toLowerCase()}`} className={className}>
                {l.label}
              </a>
            );
          })}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <LoginMenu />
          <a href={hashHref("#trip-setup")} onClick={(e) => goHash(e, "#trip-setup")}><button className="btn-primary" data-testid="nav-book-now">Book Now</button></a>
        </div>
        <button className="md:hidden text-[#FFD166]" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="md:hidden glass-nav overflow-hidden">
            <div className="container flex flex-col gap-1 py-6" data-testid="mobile-nav-content">
              {links.map((l) => (
                l.route ? (
                  <Link
                    key={l.href}
                    to={l.href}
                    onClick={() => setOpen(false)}
                    className="text-[#FFD166] py-3 font-serif text-xl border-b border-[#FFD166]/10 last:border-b-0 inline-flex items-center gap-2"
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  >
                    {l.icon ? <l.icon className="w-4 h-4" /> : null} {l.label}
                  </Link>
                ) : (
                  <a
                    key={l.href}
                    href={hashHref(l.href)}
                    onClick={(e) => { goHash(e, l.href); setOpen(false); }}
                    className="text-[#F6F1E4] py-3 font-serif text-xl border-b border-[#FFD166]/10 last:border-b-0"
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </a>
                )
              ))}
              {/* Inline auth links — replaces nested LoginMenu dropdown so single
                  tap works reliably on mobile (nested popovers get clipped by
                  the collapsing menu container's overflow-hidden). */}
              {user && user.role ? (
                <>
                  <button
                    onClick={() => { setOpen(false); nav({ admin: "/admin", employee: "/employee", customer: "/account" }[user.role] || "/"); }}
                    className="w-full flex items-center gap-3 py-3 text-left text-[#F6F1E4] border-b border-[#FFD166]/10"
                    data-testid="mobile-nav-dashboard"
                  >
                    <User className="w-5 h-5 text-[#FFD166]" />
                    <div>
                      <div className="font-serif text-lg">My {user.role === "customer" ? "Bookings" : "Dashboard"}</div>
                      <div className="text-[10px] text-[#B9C8D5]">{user.email}</div>
                    </div>
                  </button>
                  <button
                    onClick={async () => { setOpen(false); await logout(); nav("/"); }}
                    className="w-full flex items-center gap-3 py-3 text-left text-red-300"
                    data-testid="mobile-nav-logout"
                  >
                    <LogOut className="w-5 h-5" />
                    <span className="font-serif text-lg">Log out</span>
                  </button>
                </>
              ) : (
                <>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#B9C8D5]/70 pt-3 pb-1">Sign in as</div>
                  <button onClick={() => { setOpen(false); nav("/login?role=customer"); }} className="w-full flex items-center gap-3 py-3 text-left text-[#F6F1E4] border-b border-[#FFD166]/10" data-testid="mobile-nav-login-customer">
                    <Users className="w-5 h-5 text-[#FFD166]" />
                    <span className="font-serif text-lg">Customer</span>
                  </button>
                  <button onClick={() => { setOpen(false); nav("/login?role=employee"); }} className="w-full flex items-center gap-3 py-3 text-left text-[#F6F1E4] border-b border-[#FFD166]/10" data-testid="mobile-nav-login-employee">
                    <Truck className="w-5 h-5 text-[#FFD166]" />
                    <span className="font-serif text-lg">Employee</span>
                  </button>
                  <button onClick={() => { setOpen(false); nav("/login?role=admin"); }} className="w-full flex items-center gap-3 py-3 text-left text-[#F6F1E4]" data-testid="mobile-nav-login-admin">
                    <Shield className="w-5 h-5 text-[#FFD166]" />
                    <span className="font-serif text-lg">Admin</span>
                  </button>
                </>
              )}
              <a href={hashHref("#trip-setup")} onClick={(e) => { goHash(e, "#trip-setup"); setOpen(false); }} className="pt-4"><button className="btn-primary w-full" data-testid="mobile-nav-book-now">Book Now</button></a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
